/* eslint-disable no-console */
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileSearchOutlined,
  SearchOutlined,
  SyncOutlined,
  UploadOutlined
} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {
  Button,
  Flex,
  Input,
  Select,
  Table as TableAnt,
  TableProps,
  Tag
} from 'antd';
import {ColumnType} from 'antd/es/table';
import moment from 'moment';
import React, {useEffect, useState, useRef, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';

import DocumentUploadDrawerForm from '../../../components/DrawerForm/Utils/DocumentUploadDrawer';
import CADocumentManagerForm from '../../../components/Forms/CADocumentManagerForm';
import {filterSelectOptions} from '../../../components/Forms/Utils/commonFunctions';
import Loader from '../../../components/Loader/Loader';
import ActionsButton from '../../../components/Table/Utils/ActionsButton';
import TableBulkSelectionBar, {
  createBulkRowSelection
} from '../../../components/Table/Utils/TableBulkSelectionBar';
import {APITypeEnum} from '../../../services/interface/tableAPIType';
import AIDocumentManagerAPI from '../../../services/modules/AIDocumentManager/AIDocumentManagerAPI';
import efdbDatabaseDropdown from '../../../services/modules/JSON/efdbDatabaseDropdown';
import facilityDetailsAPI from '../../../services/modules/OM/facilityDetails';
import {uploadDocumentRecord} from '../../../services/modules/UploadAndJobs/uploadDocs';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import {hasUserRole, UserRole} from '../../../utils/commonFunctions';
import {yearSelect} from '../../../utils/FormUtils/yearSelect';
import mapAndFilter from '../../../utils/mapCountry';
import {shouldBlockReadOnly} from '../../../utils/SubscriptionHelper';
import {CADocumentTypeOptions, StepState} from '../Constants/commonConstants';

interface DocumentType {
  _id: string;
  sno: string;
  docType: string;
  doc_type: string;
  file_display_name: string;
  bill_type: string;
  uploadedAt: string;
  updatedAt: string;
}

const CADocument: React.FC = () => {
  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [refresh, setRefresh] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedYear, setSelectedYear] = useState<any>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [facilityId, setFacilityId] = useState<string | undefined>(undefined);
  const firstYear = yearSelect();
  const [isLoading, setIsLoading] = useState(true);
  const [dataSource, setDataSource] = useState<DocumentType[]>([]);
  const [documentType, setDocumentType] = useState<string | undefined>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [efdbData, setEfdbData] = useState([]);
  const [allFacilities, setAllFacilities] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });

  const searchInput = useRef<InputRef>(null);

  const flattenedCADocumentTypeOptions = useMemo(() => {
    const flattened: Array<{label: string; value: string}> = [];
    CADocumentTypeOptions.forEach((item) => {
      flattened.push({label: item.label, value: item.value});
      if (item.children) {
        item.children.forEach((child) => {
          flattened.push({label: child.label, value: child.value});
        });
      }
    });

    return flattened;
  }, []);

  const facilityDetailsFromStore = useCompanyStore(
    (state) => state.facilityDetails
  );

  const facilityOptions = useMemo(() => {
    return (
      facilityDetailsFromStore?.[selectedYear]?.map((item: any) => {
        return {
          isLeased: item.isLeased,
          country: item.country,
          type: item.type,
          label: item.name,
          value: item._id
        };
      }) ?? []
    );
  }, [selectedYear, facilityDetailsFromStore]);

  const SearchFilter = (
    title: string,
    dataIndex: keyof DocumentType
  ): ColumnType<DocumentType> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        return record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(value?.toString().toLowerCase());
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const columns: TableProps<DocumentType>['columns'] = [
    {
      title: 'Sr No',
      dataIndex: '',
      render: (value: any, record: any, index: number) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      }
    },
    {
      title: 'File name',
      dataIndex: 'file_display_name',
      ...SearchFilter('File name', 'file_display_name')
    },
    {
      title: 'Document Type',
      dataIndex: 'doc_type',
      filters:
        documentType === 'all'
          ? mapAndFilter(
              dataSource,
              flattenedCADocumentTypeOptions,
              'doc_type'
            ).map((item) => {
              return {text: item.label, value: item.value};
            })
          : undefined,
      onFilter: (value, record) => {
        if (documentType !== 'all') {
          return true;
        }

        return record.doc_type === value;
      },
      render: (value) => {
        for (const item of CADocumentTypeOptions) {
          if (item.value === value) return item.label;

          if (item.children) {
            const child = item.children.find((c) => c.value === value);
            if (child) return child.label;
          }
        }

        return value ?? '-';
      }
    },
    {
      title: 'Uploaded at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value) => moment(value).format('DD/MM/YYYY hh:mm A')
    },
    {
      title: 'Section',
      dataIndex: 'bill_type',
      key: 'bill_type',
      ...SearchFilter('Section', 'bill_type'),
      render: (value) => {
        for (const item of CADocumentTypeOptions) {
          // check parent
          if (item.value === value) return item.label;

          // check children
          if (item.children) {
            const child = item.children.find((c) => c.value === value);
            if (child) return child.label;
          }
        }

        return value ?? '-';
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value: string) => {
        const render: {color: string; icon: JSX.Element} = RenderTags(value);

        return (
          <Tag icon={render.icon} color={render.color} key={value}>
            {value?.replaceAll('_', ' ').toUpperCase()}
          </Tag>
        );
      }
    },
    ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
      ? ([
          {
            title: 'Action',
            dataIndex: 'operation',
            type: '',
            fixed: 'right',
            width: '200px !important',
            render: (value: any, record: any) =>
              dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                  noDelete={false}
                  isConfigurator={true}
                />
              ) : null
          }
        ] as any)
      : [])
  ];

  const RenderTags = (tag: string) => {
    switch (tag?.toLocaleLowerCase()) {
      case 'submitted':
        return {color: 'green', icon: <CheckCircleOutlined />};
      case 'ready_for_review':
      case 'reviewed':
        return {color: 'orange', icon: <FileSearchOutlined />};
      case 'uploaded':
        return {color: 'default', icon: <UploadOutlined />};
      case 'in_progress':
        return {color: 'processing', icon: <SyncOutlined spin />};
      case 'failed':
        return {color: 'error', icon: <CloseCircleOutlined />};
      default:
        return {color: 'error', icon: <CloseCircleOutlined />};
    }
  };
  const handleDelete = (_id: string[] | string) => {
    setIsLoading(true);
    Promise.all([
      uploadDocumentRecord(APITypeEnum.DELETE_ALL, {
        id: _id
      })
    ])
      .then(() => {
        setSelectedRowKeys([]);
        setIsLoading(false);
        setRefresh(true);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    const urlYear = searchParams.get('selectedYear');
    if (urlYear) {
      const isValidYear = firstYear?.some((item) => item.value === urlYear);

      if (isValidYear) {
        setSelectedYear(urlYear);
        searchParams.set('selectedYear', urlYear);
      } else {
        const defaultYear = firstYear?.[0]?.value;
        setSelectedYear(defaultYear);
        if (defaultYear) {
          searchParams.set('selectedYear', defaultYear);
        } else {
          searchParams.delete('selectedYear');
        }
      }
    } else {
      const defaultYear = firstYear?.[0]?.value;
      setSelectedYear(defaultYear);
      if (defaultYear) {
        searchParams.set('selectedYear', defaultYear);
      }
    }

    const urlDocType = searchParams.get('doc_type');
    if (urlDocType) {
      const isValidDocType =
        urlDocType === 'all' ||
        CADocumentTypeOptions.some((item) => {
          if (item.value === urlDocType) return true;

          if (item.children) {
            return item.children.some((child) => child.value === urlDocType);
          }

          return false;
        });

      if (isValidDocType) {
        setDocumentType(urlDocType);
      } else {
        setDocumentType('all');
        searchParams.delete('doc_type');
      }
    } else {
      setDocumentType('all');
    }
    setSearchParams(searchParams);
  }, []);

  useEffect(() => {
    const urlFacilityId = searchParams.get('facilityId');
    if (urlFacilityId && selectedYear && facilityOptions.length > 0) {
      const isValidFacility = facilityOptions.some(
        (option: any) => option.value === urlFacilityId
      );

      if (isValidFacility) {
        setFacilityId((currentFacilityId) => {
          if (currentFacilityId !== urlFacilityId) {
            setRefresh(true);

            return urlFacilityId;
          }

          return currentFacilityId;
        });
      } else {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('facilityId');
        setSearchParams(newSearchParams);
        setFacilityId((currentFacilityId) => {
          if (currentFacilityId === urlFacilityId) {
            return undefined;
          }

          return currentFacilityId;
        });
      }
    } else if (!urlFacilityId) {
      setFacilityId((currentFacilityId) => {
        if (currentFacilityId) {
          return undefined;
        }

        return currentFacilityId;
      });
    }
  }, [selectedYear, facilityOptions, searchParams]);

  useEffect(() => {
    if (refresh) {
      if (selectedYear) {
        setIsLoading(true);
        AIDocumentManagerAPI(facilityId, selectedYear, documentType)
          .then((res) => {
            setIsLoading(false);
            setPagination((prev) => ({
              ...prev,
              current: 1
            }));
            setDataSource(res);
            setRefresh(false);
          })
          .catch(() => {
            setIsLoading(false);
            setRefresh(false);
          });
      }
    }
  }, [refresh, selectedYear, facilityId, documentType]);

  useEffect(() => {
    Promise.all([
      efdbDatabaseDropdown(),
      facilityDetailsAPI(APITypeEnum.GET_ALL, true)
    ])
      .then(([efdbDatabaseResponse, allFacilitiesResponse]) => {
        setEfdbData(efdbDatabaseResponse);
        setAllFacilities(allFacilitiesResponse);
        setRefresh(true);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, []);

  const handleSearch = (event: {name: string; value: string}) => {
    const {name, value} = event;
    if (value) {
      searchParams.set(name, value);
      setSearchParams(searchParams);
    } else {
      searchParams.delete(name);
      setSearchParams(searchParams);
    }
  };

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  const selectedIds = (
    selectedRowKeys as Array<string | null | undefined>
  ).filter((k): k is string => Boolean(k));
  const selectedCount = selectedIds.length;

  return (
    <>
      <Flex
        vertical
        style={{padding: 10, background: '#fff', borderRadius: 10}}
      >
        <Flex justify="space-between" align="center" style={{marginBottom: 20}}>
          {/* Left side: Filters */}

          <Flex gap={15}>
            {/* Year Picker */}
            <Select
              style={{width: '120px'}}
              placeholder="Year"
              value={selectedYear}
              options={firstYear}
              size="middle"
              popupMatchSelectWidth={false}
              disabled={refresh || isLoading}
              onChange={(e) => {
                setSelectedYear(e);
                setFacilityId(undefined);
                setRefresh(true);
                handleSearch({
                  name: 'selectedYear',
                  value: e
                });
              }}
            />

            <Select
              showSearch
              placeholder="Facility Name"
              value={facilityId}
              defaultValue={undefined}
              options={facilityOptions}
              popupMatchSelectWidth={false}
              size="middle"
              disabled={refresh || isLoading}
              onChange={(e) => {
                setFacilityId(e);
                setRefresh(true);
                handleSearch({
                  name: 'facilityId',
                  value: e
                });
              }}
              filterOption={filterSelectOptions}
            />

            <Select
              showSearch
              placeholder="Document Type"
              value={documentType}
              defaultValue={undefined}
              options={[
                ...CADocumentTypeOptions.filter((item) => !item?.hidden),
                {
                  label: 'All Docs',
                  value: 'all'
                }
              ]}
              popupMatchSelectWidth={false}
              size="middle"
              style={{width: '180px'}}
              disabled={refresh || isLoading}
              onChange={(e) => {
                setDocumentType(e);
                setRefresh(true);
                handleSearch({
                  name: 'doc_type',
                  value: e
                });
              }}
              filterOption={filterSelectOptions}
            />
          </Flex>
          <Flex gap={15}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              style={{background: '#3e5c3a'}}
              onClick={() => {
                setIsEdit(false);
                setShowModel(true);
              }}
            >
              Upload Document
            </Button>
          </Flex>
        </Flex>

        {selectedCount > 0 && !shouldBlockReadOnly() && (
          <TableBulkSelectionBar
            selectedCount={selectedCount}
            selectedIds={selectedIds}
            handleDelete={handleDelete}
            onSelectAll={
              !hasUserRole(UserRole.View_Only) ? handleSelectAll : undefined
            }
            onDeselectAll={() => {
              setSelectedRowKeys([]);
            }}
            allSelectableCount={dataSource.length}
          />
        )}

        {/* Table */}
        <TableAnt
          rowSelection={createBulkRowSelection<DocumentType>({
            selectedRowKeys,
            setSelectedRowKeys,
            onSelectAll: handleSelectAll,
            onDeselectAll: () => {
              setSelectedRowKeys([]);
            }
          })}
          rowKey="_id"
          dataSource={dataSource}
          columns={columns}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize
          }}
          onChange={(page: any, _filters: any, _sorter: any, extra: any) => {
            if (extra?.action === 'filter') {
              setPagination((prev) => ({
                ...prev,
                current: 1,
                pageSize: page?.pageSize ?? prev.pageSize
              }));
            } else {
              setPagination({
                current: page.current,
                pageSize: page.pageSize
              });
            }
          }}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: refresh || isLoading
          }}
        />
      </Flex>
      {showModel && (
        <DocumentUploadDrawerForm
          efdbData={efdbData}
          allFacilities={allFacilities}
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          API={uploadDocumentRecord}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          isChecker={isEdit}
          hasOCRProcessing={true}
          FormComponent={<CADocumentManagerForm />}
          stepsTitle={['Details', 'Upload Bill', 'Verify Inputs']}
          stepsType={[
            StepState.NORMAL,
            StepState.UPLOAD_AND_VALIDATE,
            StepState.WITH_FILE_PREVIEW
          ]}
        />
      )}
    </>
  );
};

export default CADocument;
